import { EvalLeftPanelHeader } from './eval-left-panel-header';
import { ViewFilterDropdown, ViewMode } from './view-filter-dropdown';
import { WorkspaceAccordion } from './workspace-accordion';
import { WorkspaceAccordionSkeleton } from './workspace-accordion-skeleton';
import type { EvaluationWorkspace, EvaluationCalculation } from '../types';
import style from './eval-left-panel.module.scss';

interface EvalLeftPanelProps {
  workspaces: EvaluationWorkspace[];
  calculationsByWorkspace: Record<number, EvaluationCalculation[]>;
  selectedCalculationId: number | null;
  onCalculationSelect: (id: number) => void;
  onWorkspaceExpand: (id: number) => void;
  isAddDisabled: boolean;
  onAdd: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  meta: { workspaceCount: number; evaluationCount: number };
  isLoadingWorkspaces?: boolean;
  isLoadingCalculations?: boolean;
  loadingWorkspaceId: number | null;
}

/**
 * Full left-panel sidebar for the Eval 3.0 workspace view.
 * Composes the header, view filter dropdown, and workspace accordion list.
 *
 * @param {EvalLeftPanelProps} props - Component props.
 * @return {JSX.Element} The left panel.
 */
export const EvalLeftPanel = ({
  workspaces,
  calculationsByWorkspace,
  selectedCalculationId,
  onCalculationSelect,
  onWorkspaceExpand,
  isAddDisabled,
  onAdd,
  viewMode,
  onViewModeChange,
  meta,
  isLoadingWorkspaces,
  isLoadingCalculations,
  loadingWorkspaceId,
}: EvalLeftPanelProps): JSX.Element => {
  return (
    <aside className={style.panel}>
      <EvalLeftPanelHeader
        workspaceCount={meta.workspaceCount}
        evaluationCount={meta.evaluationCount}
        isAddDisabled={isAddDisabled}
        onAdd={onAdd}
      />

      <div className={style.filterSection}>
        <ViewFilterDropdown value={viewMode} onChange={onViewModeChange} />
      </div>

      <div className={style.workspaces}>
        {isLoadingWorkspaces ? (
          <>
            <WorkspaceAccordionSkeleton />
            <WorkspaceAccordionSkeleton />
            <WorkspaceAccordionSkeleton />
          </>
        ) : (
          workspaces.map((ws) => (
            <WorkspaceAccordion
              key={ws.id}
              workspace={ws}
              calculations={calculationsByWorkspace[ws.id] ?? []}
              onExpand={() => onWorkspaceExpand(ws.id)}
              selectedCalculationId={selectedCalculationId}
              onCalculationSelect={onCalculationSelect}
              isLoadingCalculations={
                isLoadingCalculations && ws.id === loadingWorkspaceId
              }
            />
          ))
        )}
      </div>
    </aside>
  );
};
